import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, Brain, Timer, Heart, ArrowRight, Moon } from 'lucide-react';
import { ActivityReport } from './ActivityReport';

interface MindfulBreathingProps {
  onClose: () => void;
  initialStressLevel: number;
  pattern?: {
    inhale: number;
    holdIn?: number;
    exhale: number;
    holdOut?: number;
  };
  title?: string;
  description?: string;
}

export function MindfulBreathing({ 
  onClose, 
  initialStressLevel,
  pattern = { inhale: 4, holdIn: 2, exhale: 6 },
  title = "Mindful Breathing",
  description = "Follow the breathing pattern to find your calm."
}: MindfulBreathingProps) {
  const [currentPhase, setCurrentPhase] = useState<'inhale' | 'holdIn' | 'exhale' | 'holdOut'>('inhale');
  const [timeLeft, setTimeLeft] = useState(pattern.inhale);
  const [isActive, setIsActive] = useState(false);
  const [totalBreaths, setTotalBreaths] = useState(0);
  const [duration, setDuration] = useState(5); // minutes
  const [showReport, setShowReport] = useState(false);

  useEffect(() => {
    if (!isActive) return;

    const interval = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          switch (currentPhase) {
            case 'inhale':
              setCurrentPhase(pattern.holdIn ? 'holdIn' : 'exhale');
              return pattern.holdIn || pattern.exhale;
            case 'holdIn':
              setCurrentPhase('exhale');
              return pattern.exhale;
            case 'exhale':
              setCurrentPhase(pattern.holdOut ? 'holdOut' : 'inhale');
              if (!pattern.holdOut) setTotalBreaths(prev => prev + 1);
              return pattern.holdOut || pattern.inhale;
            case 'holdOut':
              setCurrentPhase('inhale');
              setTotalBreaths(prev => prev + 1);
              return pattern.inhale;
          }
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isActive, currentPhase, pattern]);

  const getPhaseText = () => {
    switch (currentPhase) {
      case 'inhale': return 'Breathe In';
      case 'holdIn': return 'Hold';
      case 'exhale': return 'Breathe Out';
      case 'holdOut': return 'Hold';
    }
  };

  const getCircleScale = () => {
    switch (currentPhase) {
      case 'inhale': return 'scale(1.2)';
      case 'holdIn': return 'scale(1.2)';
      case 'exhale': return 'scale(0.8)';
      case 'holdOut': return 'scale(0.8)';
    }
  };

  const handleEndSession = () => {
    setIsActive(false);
    setShowReport(true);
  };

  if (showReport) {
    return (
      <ActivityReport
        initialStressLevel={initialStressLevel}
        duration={duration}
        activityType="Mindful Breathing"
        onClose={onClose}
      />
    );
  }

  return (
    <div className="fixed inset-0 bg-background/95 backdrop-blur-xl z-[100] overflow-y-auto mindful-scrollbar">
      <div className="min-h-screen p-6">
        <div className="max-w-4xl mx-auto space-y-8">
          {/* Header */}
          <div className="flex items-center justify-between sticky top-0 bg-background/50 backdrop-blur-sm py-4 z-50">
            <div className="flex items-center gap-3">
              <div className="relative">
                <Brain className="w-6 h-6 text-primary" />
                <div className="absolute inset-0 bg-primary/20 blur-xl rounded-full" />
              </div>
              <h2 className="text-xl font-semibold">{title}</h2>
            </div>
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <p className="text-center text-muted-foreground">{description}</p>

          {/* Duration Selection */}
          {!isActive && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Choose Duration</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[5, 15, 30, 45, 60, 90, 120, 180].map((mins) => (
                  <button
                    key={mins}
                    onClick={() => setDuration(mins)}
                    className={`relative p-4 rounded-xl overflow-hidden group ${
                      duration === mins ? 'mindful-timer' : 'bg-muted hover:bg-muted/70'
                    }`}
                  >
                    {duration === mins && (
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent animate-shine" />
                    )}
                    <span className={duration === mins ? 'text-primary' : ''}>
                      {mins >= 60 ? `${mins / 60} hr${mins > 60 ? 's' : ''}` : `${mins} min`}
                    </span>
                  </button>
                ))}
              </div>
              <button
                onClick={() => setIsActive(true)}
                className="w-full mindful-timer p-4 rounded-xl text-primary font-medium relative group overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent animate-shine" />
                <div className="relative flex items-center justify-center gap-2">
                  Begin Practice
                  <ArrowRight className="w-4 h-4" />
                </div>
              </button>
            </div>
          )}

          {/* Breathing Animation */}
          {isActive && (
            <div className="flex flex-col items-center justify-center space-y-8">
              <div className="relative">
                <div 
                  className="breathing-circle w-64 h-64 flex items-center justify-center"
                  style={{
                    transform: getCircleScale(),
                    transition: 'transform 2s cubic-bezier(0.4, 0, 0.2, 1)'
                  }}
                >
                  <div className="text-4xl font-bold mindful-gradient-text">
                    {timeLeft}
                  </div>
                </div>
                <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-purple-500/20 rounded-full blur-3xl" />
              </div>

              <div className="text-2xl font-medium mindful-gradient-text">
                {getPhaseText()}
              </div>

              <div className="text-sm text-muted-foreground">
                Total Breaths: {totalBreaths}
              </div>

              <button
                onClick={handleEndSession}
                className="px-6 py-3 rounded-lg bg-muted text-muted-foreground hover:bg-muted/70 transition-colors"
              >
                End Session
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}